import type { ParsedUrlQuery } from 'node:querystring'
import React, { useMemo } from 'react'
import { useRouter } from 'next/router'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { getGlobalTriggerState, getTriggerTypes } from '~/dataProcessor/api/api'
import { useAppInfo } from '~/dataProcessor/hooks/app'
import {
  getTriggerInstancesActive,
  postInternalTriggerLogs,
} from '~/generated/client'
import { useRouterParsed } from '../utils/useRouterParse'

export const QUERY_KEYS = {
  getTriggerDataQueryKey: (query: ParsedUrlQuery) => [
    'TRIGGERS',
    query.page ?? '1',
    query.name ?? '',
    query.connectorId ?? '',
    query.connectionId ?? '',
    query.showDisabled ?? 'false',
  ],
  getTriggerQueryKey: (appName: string | null, query: ParsedUrlQuery) => [
    'TRIGGERS',
    appName,
    query.page ?? '1',
  ],
}

export const useTriggerDisabledBanner = () => {
  const { isNonSidebarPage } = useAppInfo()
  const { data: triggersInfo, refetch } = useQuery(
    ['triggerState'],
    getGlobalTriggerState,
    {
      // Only fetch trigger state on authenticated pages (pages with sidebar)
      // Skip fetching on public/unauthenticated pages
      enabled: !isNonSidebarPage,
    },
  )
  const [showTriggerDisabledBanner, setShowTriggerDisabledBanner] =
    React.useState(false)

  React.useEffect(() => {
    if (triggersInfo && !triggersInfo.triggers_enabled) {
      setShowTriggerDisabledBanner(true)
    } else {
      setShowTriggerDisabledBanner(false)
    }
  }, [triggersInfo?.triggers_enabled])

  return {
    showTriggerDisabledBanner: showTriggerDisabledBanner && !isNonSidebarPage,
    refetch,
  }
}

export const useGetTriggerInstanceData = () => {
  const { query } = useRouter()
  const showDisabled = query.showDisabled === 'true' ? 'true' : 'false'
  const { data, isLoading, isError, isSuccess, refetch } = useQuery({
    queryKey: QUERY_KEYS.getTriggerDataQueryKey(query),
    queryFn: () =>
      getTriggerInstancesActive({
        page: Number(query?.page ?? 1),
        triggerNames: query.name ? [query.name as string] : undefined,
        authConfigIds: query.connectorId
          ? [query.connectorId as string]
          : undefined,
        connectedAccountIds: query.connectionId
          ? [query.connectionId as string]
          : undefined,
        showDisabled: showDisabled === 'true' ? true : false,
      }),
    retry: false,
    refetchOnWindowFocus: true,
    cacheTime: 0,
    staleTime: 0,
  })

  return {
    triggerData: data,
    isLoading,
    isError,
    isSuccess,
    refetch,
  }
}

export const useGetTriggerData = (appName: string) => {
  const { page, query } = useRouterParsed()

  const {
    data: triggerData,
    isLoading,
    isError,
    isSuccess,
    refetch,
  } = useQuery({
    queryKey: QUERY_KEYS.getTriggerQueryKey(
      appName as unknown as string,
      query,
    ),
    queryFn: () =>
      getTriggerTypes({
        page: Number(page ?? 1),
        slug: appName || undefined,
        // authConfigId: authConfigId,
        // connectedAccountIds: query.connectionId as string | undefined,
      }),
    retry: true,
  })
  return {
    triggerData,
    isLoading,
    isError,
    isSuccess,
    refetch,
  }
}

/**
 * Custom hook for fetching and managing trigger logs with infinite scroll
 * Uses react-query for data fetching, caching and pagination
 * @returns Object containing logs data and utility functions for pagination
 */
export const useTriggerLogs = ({
  filter: { search, time, status, integrationId, entityId, pageSize = 10 },
}: {
  filter: {
    time?: '30m' | '6h' | '1d' | '1w' | '1month' | '1y'
    status?: 'all' | 'success' | 'error'
    integrationId?: string
    entityId?: string
    search?: string
    pageSize?: number
  }
}) => {
  const { data: fields } = useQuery({
    queryKey: ['triggerFields'],
    queryFn: () => getTriggerInstancesActive(),
  })

  const {
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    data,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ['triggerLogs', search, time, status, integrationId, entityId],
    queryFn: ({ pageParam = null }) =>
      postInternalTriggerLogs({
        requestBody: {
          cursor: pageParam,
          limit: pageSize,
          search: search,
          time: time,
          status: status,
          integrationId: integrationId,
          entityId: entityId,
        },
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })

  // eliminate null values from the logs
  const logs = useMemo(
    () =>
      data?.pages?.flatMap((page) =>
        Array.isArray(page?.data) ? page.data.filter(Boolean) : [],
      ) ?? [],
    [data?.pages],
  )

  return {
    logs,
    isFetching,
    fields,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isError,
    isFetchingNextPage,
    refetch,
  }
}
